import { Checkbox, PropertyValue } from '@affine/component';
import { DocService } from '@affine/core/modules/doc';
import { useLiveData, useService } from '@toeverything/infra';
import { type ChangeEvent, useCallback } from 'react';

import type { PropertyValueProps } from '../properties/types';
import * as styles from './edgeless-disabled.css';

export const EdgelessDisabledValue = ({ readonly }: PropertyValueProps) => {
  const doc = useService(DocService).doc;

  const edgelessDisabled = useLiveData(doc.edgelessDisabled$);
  const primaryMode = useLiveData(doc.primaryMode$);

  // positive semantics in the UI: checkbox checked = canvas mode enabled
  const enabled = !edgelessDisabled;

  const setEnabled = useCallback(
    (nextEnabled: boolean) => {
      if (readonly) return;
      doc.setEdgelessDisabled(!nextEnabled);
      // when disabling canvas, fall back the default mode to page
      if (!nextEnabled && primaryMode === 'edgeless') {
        doc.setPrimaryMode('page');
      }
    },
    [doc, primaryMode, readonly]
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setEnabled(e.target.checked);
    },
    [setEnabled]
  );

  const toggle = useCallback(() => {
    setEnabled(!enabled);
  }, [enabled, setEnabled]);

  return (
    <PropertyValue className={styles.property} onClick={toggle} readonly>
      <Checkbox
        data-testid="toggle-canvas-mode-checkbox"
        checked={enabled}
        onChange={onChange}
        className={styles.checkbox}
        disabled={readonly}
      />
    </PropertyValue>
  );
};
